import { readonly, ref } from "vue";
import { useNotificationBus } from "@/composables/useNotificationBus";
import accessTokenManager from "@/utils/accessToken";
import { logger } from "@/utils/logger";
import { useNotificationDisplayPolicy } from "@/composables/useNotificationDisplayPolicy";

export type NotificationSocketState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

const HEARTBEAT_TIMEOUT_MS = 45_000;
const HISTORY_PAGE_SIZE = 20;
const state = ref<NotificationSocketState>("idle");

let socket: WebSocket | null = null;
let activeUsername = "";
let activeOwner = "center";
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let watchdogTimer: number | null = null;
let lastMessageAt = 0;
let stopped = true;
let opening = false;
let openingEpoch = 0;

export function buildNotificationSocketUrl(username: string, token = ""): string {
  const wsBaseUrl = (import.meta.env.VITE_WS_BASE_URL || "/ws").replace(/\/$/, "");
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const query = [`operator=${encodeURIComponent(username)}`, `history_limit=${HISTORY_PAGE_SIZE}`];
  if (token) query.push(`token=${encodeURIComponent(token)}`);
  return `${protocol}//${window.location.host}${wsBaseUrl}/v2/notification/?${query.join("&")}`;
}

function clearReconnectTimer(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function closeSocket(): void {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
  try {
    socket.close();
  } catch {
    // Browser may reject close while the handshake is being torn down.
  }
  socket = null;
}

function scheduleReconnect(): void {
  if (stopped || !activeUsername || reconnectTimer !== null) return;
  reconnectAttempt += 1;
  state.value = "reconnecting";
  const delay = Math.min(1_000 * 2 ** Math.min(reconnectAttempt, 5), 30_000);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    void connect();
  }, delay);
}

async function connect(): Promise<void> {
  if (stopped || !activeUsername) return;
  if (opening) return;
  if (
    socket &&
    (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)
  ) {
    return;
  }

  state.value = reconnectAttempt > 0 ? "reconnecting" : "connecting";
  const notificationBus = useNotificationBus();
  const expectedUsername = activeUsername;
  const expectedEpoch = ++openingEpoch;
  opening = true;
  try {
    // First realtime delivery waits for admission; history/refresh never replay toasts.
    await useNotificationDisplayPolicy().refresh();
    const token = await accessTokenManager.getAccessToken();
    if (stopped || activeUsername !== expectedUsername || openingEpoch !== expectedEpoch) return;
    if (!token) throw new Error("notification WebSocket access token is empty");
    socket = new WebSocket(buildNotificationSocketUrl(activeUsername, token));
    socket.onopen = () => {
      if (stopped || openingEpoch !== expectedEpoch) return;
      reconnectAttempt = 0;
      lastMessageAt = Date.now();
      state.value = "connected";
    };
    socket.onmessage = (event: MessageEvent) => {
      if (stopped || activeUsername !== expectedUsername || openingEpoch !== expectedEpoch) return;
      lastMessageAt = Date.now();
      try {
        const payload = JSON.parse(String(event.data));
        const messages = Array.isArray(payload) ? payload : [payload];
        for (const message of messages) {
          if (message?.table === "notification") {
            notificationBus.push(message);
          } else if (message?.table === "notification_history_end") {
            notificationBus.finishHistoryLoad(message.has_more === true, message.oldest_ts);
          }
        }
      } catch (error) {
        logger.warn("[NotificationSocket] 消息解析失败:", error);
      }
    };
    socket.onerror = (error) => {
      logger.warn("[NotificationSocket] WebSocket error:", error);
    };
    socket.onclose = () => {
      if (openingEpoch !== expectedEpoch) return;
      socket = null;
      notificationBus.cancelHistoryLoad();
      if (!stopped) scheduleReconnect();
    };
  } catch (error) {
    logger.warn("[NotificationSocket] 建连失败:", error);
    socket = null;
    scheduleReconnect();
  } finally {
    if (openingEpoch === expectedEpoch) opening = false;
  }
}

function startWatchdog(): void {
  if (watchdogTimer !== null || typeof window === "undefined") return;
  watchdogTimer = window.setInterval(() => {
    if (
      socket?.readyState === WebSocket.OPEN &&
      lastMessageAt > 0 &&
      Date.now() - lastMessageAt > HEARTBEAT_TIMEOUT_MS
    ) {
      logger.warn("[NotificationSocket] heartbeat timeout, reconnecting");
      closeSocket();
      scheduleReconnect();
    }
  }, 5_000);
}

function start(username: string, owner = "center"): void {
  const normalized = username.trim();
  if (!normalized) {
    stop(owner);
    return;
  }
  const notificationBus = useNotificationBus();
  notificationBus.setUserScope(normalized);
  if (!stopped && normalized === activeUsername) {
    activeOwner = owner;
    void connect();
    return;
  }
  stop(activeOwner);
  activeOwner = owner;
  activeUsername = normalized;
  stopped = false;
  reconnectAttempt = 0;
  startWatchdog();
  void connect();
}

function stop(owner = "center"): void {
  // A previous route must not close the next route's shared connection.
  if (owner !== activeOwner) return;
  stopped = true;
  clearReconnectTimer();
  closeSocket();
  if (watchdogTimer !== null) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
  state.value = activeUsername ? "disconnected" : "idle";
  activeUsername = "";
  reconnectAttempt = 0;
  lastMessageAt = 0;
  openingEpoch += 1;
  opening = false;
  useNotificationBus().cancelHistoryLoad();
}

function loadMore(): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  const notificationBus = useNotificationBus();
  const beforeTs = notificationBus.beginHistoryLoad();
  if (beforeTs === null) return;
  try {
    socket.send(
      JSON.stringify({
        action: "load_more",
        before_ts: beforeTs,
        limit: HISTORY_PAGE_SIZE,
      }),
    );
  } catch (error) {
    notificationBus.cancelHistoryLoad();
    logger.warn("[NotificationSocket] 加载历史失败:", error);
  }
}

export function useNotificationSocket() {
  return {
    state: readonly(state),
    start,
    stop,
    loadMore,
  };
}
